import { SelfServiceError } from './Errors/SelfServiceError';
import { OrderDetails, TicketHolder } from '@/Services/BKAPI/models';
import { fromAPITicketHolder } from '@/Services/BKAPI/Reducers/SelfServiceReducer';
import { fromAPIToOrderDetails } from '@/Services/BKAPI/Reducers/OrderDetailsReducer';
import {
  SelfServiceApi,
  SelfServiceApiCreateSelfServiceTicketHolderRequest,
  SelfServiceApiUpdateSelfServiceTicketHolderRequest,
} from '@bookingkit/api-v4';
import { TicketHolderDataType } from '@/components/TicketHolder/composables/types';
import { format } from 'date-fns';

export class SelfService {
  private readonly connection: SelfServiceApi;
  private readonly authToken?: string;
  constructor(connection: SelfServiceApi, authToken?: string) {
    this.connection = connection;
    this.authToken = authToken;
  }

  private getHeaders(): { authorization?: string } {
    return this.authToken ? { authorization: `Bearer ${this.authToken}` } : {};
  }

  async patchTicketHolder(
    vendorId: string,
    orderId: string,
    ticketHolderId: string,
    ticketHolderPatchRequest: any
  ): Promise<TicketHolder> {
    const params = ticketHolderPatchRequest;
    if (ticketHolderPatchRequest.dateOfBirth instanceof Date) {
      const formattedDate = format(ticketHolderPatchRequest.dateOfBirth, 'yyyy-MM-dd');
      params.dateOfBirth = formattedDate;
    }

    const headers = this.getHeaders();
    const { data } = await this.connection.updateSelfServiceTicketHolder(
      {
        vendorId,
        orderId,
        ticketHolderId,
        updateSelfServiceTicketHolderRequest: params,
      } as SelfServiceApiUpdateSelfServiceTicketHolderRequest,
      { headers }
    );
    if (!data) {
      throw SelfServiceError.fromMissingProperty('data', 'object', data);
    }
    return fromAPITicketHolder(data);
  }

  async createTicketHolder(
    vendorId: string,
    orderId: string,
    ticketHolderData: TicketHolderDataType
  ): Promise<TicketHolder> {
    const headers = this.getHeaders();
    const params = ticketHolderData;
    if (ticketHolderData.dateOfBirth instanceof Date) {
      params.dateOfBirth = format(ticketHolderData.dateOfBirth, 'yyyy-MM-dd');
    }
    const { data } = await this.connection.createSelfServiceTicketHolder(
      {
        vendorId,
        orderId,
        createSelfServiceTicketHolderRequest: params,
      } as SelfServiceApiCreateSelfServiceTicketHolderRequest,
      { headers }
    );

    if (!data) {
      throw SelfServiceError.fromMissingProperty('data', 'object', data);
    }
    return fromAPITicketHolder(data);
  }

  async getOrderDetails(vendorId: string, orderId: string): Promise<OrderDetails> {
    const headers = this.getHeaders();

    const { data } = await this.connection.getSelfServiceOrder({ vendorId, orderId }, { headers });
    if (!data) {
      throw SelfServiceError.fromMissingProperty('data', 'object', data);
    }
    return fromAPIToOrderDetails(data);
  }

  async getTicketHolderList(vendorId: string, orderId: string): Promise<TicketHolder[]> {
    const headers = this.getHeaders();
    const { data } = await this.connection.listSelfServiceTicketHolders({ vendorId, orderId }, { headers });
    if (!data?.data) {
      throw SelfServiceError.fromMissingProperty('data', 'object', data);
    }
    return data.data.map(fromAPITicketHolder);
  }

  async getTicketHolderPhoto(args: { vendorId: string; orderId: string; ticketHolderId: string }): Promise<string> {
    const headers = this.getHeaders();
    const response = await this.connection.getSelfServiceTicketHolderPhoto(args, { headers });
    return response?.data?.url;
  }
  async postPhoto(args: {
    vendorId: string;
    orderId: string;
    ticketHolderId: string;
    photo: File;
    tosAcceptance: boolean;
  }): Promise<void> {
    const headers = this.getHeaders();

    const { vendorId, orderId, ticketHolderId, photo, tosAcceptance } = args;
    await this.connection.uploadSelfServicePhoto(
      {
        vendorId,
        orderId,
        ticketHolderId,
        photo,
        tosAcceptance: tosAcceptance ? (1 as unknown as true) : (0 as unknown as false),
      },
      {
        headers,
      }
    );
  }
}

export default SelfService;
